import { recallHoldStore } from '../recall-agent/derive';
import { getAgentHold, holdAgent, resumeAgent } from '../team-hold/teamHold';
import type { RaAgentData, RaChannel, RaWeek } from './types';

/**
 * Every headline on the reminders agent page, **computed** (amendment 25 §6).
 *
 * Same treatment as the recall agent page, for the same reason: the figures were
 * written down separately and drifted. *Per 100 sent → 67 confirmed* divided the
 * confirmations by **sends**, which counts a patient who got four messages four
 * times; the denominator it wanted was appointments. The eyebrow claimed *working
 * since 8:00am*, a timestamp nothing produced.
 */

/** The headline — the segments' sum, so the breakdown cannot disagree with it. */
export function raConfirmed(data: RaAgentData): number {
  return data.hero.segments.reduce((n, s) => n + s.value, 0);
}

/**
 * `confirmed ÷ appointments × 100`. **Appointments, not sends** — the old footer
 * said *per 100 sent*, and a patient who got the 14-day email, two texts and a
 * call was four sends and one appointment.
 */
export function raPerHundredAppointments(data: RaAgentData): number {
  const appts = data.hero.appointments;
  return appts === 0 ? 0 : Math.round((raConfirmed(data) / appts) * 100);
}

export function raChannelRate(ch: RaChannel): number {
  return ch.sent === 0 ? 0 : Math.round((ch.confirmed / ch.sent) * 100);
}

/** Confirmations across the channels — asserted equal to `raConfirmed` (§8). */
export function raChannelsConfirmed(data: RaAgentData): number {
  return data.channels.reduce((n, c) => n + c.confirmed, 0);
}

/** Messages across the channels — asserted equal to the *Sent* card (§8). */
export function raChannelsSent(data: RaAgentData): number {
  return data.channels.reduce((n, c) => n + c.sent, 0);
}

export function raWeeklyAxisMax(weeks: RaWeek[]): number {
  const top = weeks.reduce((n, w) => Math.max(n, w.confirmed), 0);
  return Math.max(100, Math.ceil(top / 50) * 50);
}

/**
 * `5 STEPS · 4 ON · WORKING`, or `… · ON HOLD` (§6).
 *
 * *Before the visit* is the sequence this counts — post-op checks, the no-show
 * follow-up and the review ask are their own sections, and counting them here
 * would make the eyebrow disagree with the Settings page's footer.
 */
export function raEyebrow(data: RaAgentData, onHold: boolean): string {
  const before = data.steps.filter((s) => s.before === true);
  const on = before.filter((s) => s.off !== true).length;
  return `${String(before.length)} steps · ${String(on)} on · ${onHold ? 'on hold' : 'working'}`;
}

/* ---- hold ---------------------------------------------------------------- *
 * Reminders holds exactly as Recall does (§6 → amendment 24 §6): one flag, read
 * by the eyebrow, the button and the AI Team roster row.
 * -------------------------------------------------------------------------- */

export const remindersHoldStore = { get onHold(): boolean { return getAgentHold('reminders').held; } };

export function setRemindersHold(onHold: boolean): void {
  if (onHold) holdAgent('reminders', { by: 'Ava Thompson', since: '9:10 am', waiting: 38 });
  else resumeAgent('reminders');
}

/** True when either agent is held — for a roster that shows both. */
export function anyAgentHeld(): boolean {
  return remindersHoldStore.onHold || recallHoldStore.onHold;
}
